import { useRef, useState } from 'react';

import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/cn';
import { formatFileSize } from '@/lib/format';

export interface FileDropzoneProps {
  onSelect: (file: File) => void;
  /** Comma-separated accept list, e.g. ".pdf,.docx". */
  accept?: string;
  maxSizeBytes?: number;
  disabled?: boolean;
  /** The chosen file, shown in place of the prompt. */
  file?: File | null;
  onClear?: () => void;
  /**
   * Replaces the default "Up to 25 MB" line.
   *
   * Where a zone accepts less than the app does - ID scans take PDF, JPG and
   * PNG only - saying so up front beats an error after the picker closes.
   */
  hint?: string;
  /** Compact spacing, for a zone sitting inside a card rather than a drawer. */
  dense?: boolean;
}

/**
 * Click-or-drop file input.
 *
 * The native `<input type="file">` stays in the DOM but hidden: it is what
 * opens the OS picker and what keyboard users reach, while the visible surface
 * is entirely ours. Size and type are checked here so the user finds out before
 * a round trip, though the server re-checks both regardless.
 */
export function FileDropzone({
  onSelect,
  accept,
  maxSizeBytes,
  disabled = false,
  file,
  onClear,
  hint,
  dense = false,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function validateAndSelect(candidate: File): void {
    setError(null);

    if (maxSizeBytes && candidate.size > maxSizeBytes) {
      setError(
        `That file is ${formatFileSize(candidate.size)}. The limit is ${formatFileSize(maxSizeBytes)}.`,
      );
      return;
    }

    onSelect(candidate);
  }

  if (file) {
    return (
      <div className="flex items-center gap-2.5 rounded-md border border-border bg-surface-muted p-3">
        <Icon name="document" size={18} className="shrink-0 text-content-muted" />

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-content">{file.name}</p>
          <p className="text-xs text-content-muted">{formatFileSize(file.size)}</p>
        </div>

        {onClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Remove file"
            className="shrink-0 rounded-sm p-1 text-content-muted hover:text-danger"
          >
            <Icon name="close" size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          inputRef.current?.click();
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => {
          setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);

          const dropped = event.dataTransfer.files[0];
          if (dropped) {
            validateAndSelect(dropped);
          }
        }}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-1.5 rounded-md border border-dashed transition-colors',
          dense ? 'px-3 py-4' : 'px-4 py-6',
          'disabled:cursor-not-allowed disabled:opacity-50',
          dragging
            ? 'border-primary bg-primary-soft'
            : 'border-border-strong hover:border-content-subtle hover:bg-surface-muted',
        )}
      >
        <Icon name="upload" size={dense ? 15 : 18} className="text-content-subtle" />
        <span className={cn('text-content', dense ? 'text-xs' : 'text-sm')}>
          <span className="font-medium text-primary">Choose a file</span> or drag it here
        </span>
        {(hint ?? maxSizeBytes) && (
          <span className="text-2xs text-content-subtle">
            {hint ?? `Up to ${formatFileSize(maxSizeBytes ?? 0)}`}
          </span>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          if (chosen) {
            validateAndSelect(chosen);
          }
          /* Reset so re-picking the same file still fires a change event. */
          event.target.value = '';
        }}
      />

      {error && (
        <p role="alert" className="mt-1.5 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
